/**
 * The evidence step's document list, and the one decision that depends on it.
 *
 * Owns exactly three things: which files exist, what state each is in, and whether
 * the engagement may start yet. It does not render, and it does not know what a
 * dropzone looks like — `AdvisoryEvidenceScreen` stays presentational and takes
 * this hook's result from the page, mirroring how `onStart` is already threaded.
 *
 * **Why `readyCount` is the interesting output.** It selects the retrieval profile.
 * Simon's configuration puts `doc_intel` on `sein` and `output_2`, and the
 * supervisor exposes it as `simon-docintel`; the plain `simon` profile has NO
 * doc-intel node at all. Send `simon` with documents in the collection and they are
 * ingested, stored, paid for — and never read, by a run that completes and looks
 * healthy. Send `simon-docintel` with an EMPTY collection and the agent falls back
 * to the conversation's own node outputs, which the engagement harness measured as
 * 2-6x slower and truncating 3 of 6 node-runs against 1 of 27 with a document.
 *
 * So neither profile is right in general, and the frontend is the only party that
 * knows which applies — it is the side that watched the user upload. That makes this
 * a one-line decision here rather than a flag anyone has to remember:
 * `readyCount > 0`.
 *
 * **Why `blocked` is not the same as "uploading".** A document still ingesting when
 * the run starts is a document the engagement never sees: a node reads its
 * collection as it stands when its turn runs. So the start action must stay closed
 * while anything is non-terminal — but NOT because of a failed file, which is
 * terminal and whose owner may reasonably choose to start without it.
 */

import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../redux/store';
import { getValidToken } from '../../redux/slices/userSlice';
import {
  ingestAdvisoryDocument,
  type AdvisoryDocumentRecord,
} from '../../services/advisoryDocumentService';
import { ADVISORY_DOCUMENT_UPLOAD_CODE } from '../../redux/slices/advisory/advisoryErrorCodes';

/** The two profiles the supervisor accepts. Mirrors agent-svc's own closed set. */
export type AdvisoryNodeProfile = 'simon' | 'simon-docintel';

export interface UseAdvisoryDocumentsResult {
  documents: AdvisoryDocumentRecord[];
  /** True while any document is still uploading or ingesting. */
  blocked: boolean;
  /** Documents docintel confirmed `ready`. The only ones a run can actually read. */
  readyCount: number;
  /**
   * The profile a run should use, derived from `readyCount`. See the module
   * docstring for why this is not a user-facing choice.
   */
  nodeProfile: AdvisoryNodeProfile;
  addFiles: (files: readonly File[]) => void;
  /** Re-run a failed document. Mints a fresh document id — the old one is spent. */
  retry: (documentId: string) => void;
  /** Forget a document locally. Does NOT un-ingest it; see the implementation note. */
  remove: (documentId: string) => void;
}

function isTerminal(record: AdvisoryDocumentRecord): boolean {
  return record.state === 'ready' || record.state === 'failed';
}

export function useAdvisoryDocuments(collectionId: string | null): UseAdvisoryDocumentsResult {
  const dispatch = useDispatch<AppDispatch>();
  const [documents, setDocuments] = useState<AdvisoryDocumentRecord[]>([]);
  // Keyed by document id so an out-of-order progress callback updates the row it
  // belongs to rather than whichever index it happened to hold. Files complete in
  // whatever order docintel finishes them, which is not the order they were added.
  const filesById = useRef<Map<string, File>>(new Map());

  const upsert = useCallback((record: AdvisoryDocumentRecord) => {
    setDocuments(previous => {
      const index = previous.findIndex(d => d.documentId === record.documentId);
      return index === -1
        ? [...previous, record]
        : previous.map((d, i) => (i === index ? record : d));
    });
  }, []);

  const start = useCallback(
    async (file: File): Promise<void> => {
      if (!collectionId) {
        // Guarded rather than thrown: the caller keeps the dropzone closed until a
        // handle exists, so reaching here is a bug, and a thrown error inside a
        // fire-and-forget handler would surface as an unhandled rejection.
        return;
      }
      let token: string;
      try {
        token = await dispatch(getValidToken()).unwrap();
      } catch {
        // No token, no upload. Reported as a retryable upload failure — the file is
        // untouched and a fresh sign-in makes the same attempt valid.
        upsert({
          documentId: `pending-${file.name}-${file.size}`,
          fileName: file.name,
          sizeBytes: file.size,
          collectionId,
          state: 'failed',
          errorCode: ADVISORY_DOCUMENT_UPLOAD_CODE,
          canonicalDocumentId: null,
          pageCount: null,
          chunkCount: null,
        });
        return;
      }

      await ingestAdvisoryDocument({
        file,
        collectionId,
        token,
        onProgress: record => {
          filesById.current.set(record.documentId, file);
          upsert(record);
        },
      }).then(final => {
        // Recorded again on the terminal result, not only from `onProgress`. The
        // file is what `retry` needs, and keying it off a progress callback would
        // make retry silently impossible for any document whose first observed
        // state was already terminal.
        filesById.current.set(final.documentId, file);
        upsert(final);
      });
    },
    [collectionId, dispatch, upsert]
  );

  const addFiles = useCallback(
    (files: readonly File[]) => {
      // Every file starts its own independent sequence. Serialising them would make
      // the slowest document gate the rest for no benefit — each is a separate
      // docintel Job.
      files.forEach(file => {
        void start(file);
      });
    },
    [start]
  );

  const retry = useCallback(
    (documentId: string) => {
      const file = filesById.current.get(documentId);
      if (!file) {
        return;
      }
      // The failed row is dropped and the file re-run under a NEW document id. A
      // document id is spent once uploaded — reusing it would either collide or
      // resolve onto the failed record, and neither is a retry.
      filesById.current.delete(documentId);
      setDocuments(previous => previous.filter(d => d.documentId !== documentId));
      void start(file);
    },
    [start]
  );

  const remove = useCallback((documentId: string) => {
    // Local only, and deliberately so. A ready document is already ingested into the
    // collection, and this client has no erasure path — dropping the row would claim
    // an un-ingest that did not happen. Only non-ready rows are removable; the caller
    // does not offer the control for a ready one.
    filesById.current.delete(documentId);
    setDocuments(previous => previous.filter(d => d.documentId !== documentId));
  }, []);

  const readyCount = documents.filter(d => d.state === 'ready').length;

  return {
    documents,
    blocked: documents.some(d => !isTerminal(d)),
    readyCount,
    nodeProfile: readyCount > 0 ? 'simon-docintel' : 'simon',
    addFiles,
    retry,
    remove,
  };
}
